import { useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Image as ImageIcon, Trash2, Upload, X } from 'lucide-react'
import { ApiError, type MediaAsset, type MediaUse } from '@/api/client'
import { useDeleteMedia, useMedia, useUploadMedia } from '@/lib/queries'
import { Button, ConfirmDialog, Dialog, EmptyState, ErrorState, IconButton, MediaThumb, PageHeader, Skeleton, errorText, mediaFileUrl, useToast } from '@/ui'
import { useOverlay } from '@/ui/overlay'

const KIND_LABEL: Record<MediaUse['kind'], string> = { draft: '캠페인 글', desk_post: '데스크 글' }

/** The posts that stop a file from being deleted, from the 409's body. */
function usesOf(e: unknown): MediaUse[] | null {
  if (!(e instanceof ApiError) || e.status !== 409) return null
  const uses = (e.body as { used_by?: MediaUse[] } | null)?.used_by
  return Array.isArray(uses) && uses.length > 0 ? uses : null
}

const kb = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)}MB` : `${(n / 1024).toFixed(0)}KB`)

/** Full-size view. Escape and a click outside close it — a viewer that can
 *  only be dismissed by a small × is worse than no viewer. */
function Lightbox({ asset, onClose }: { asset: MediaAsset; onClose: () => void }) {
  const panel = useRef<HTMLDivElement>(null)
  useOverlay(true, onClose, panel)
  return createPortal(
    <div
      ref={panel}
      role="dialog"
      aria-modal="true"
      aria-label={asset.Filename}
      tabIndex={-1}
      onClick={onClose}
      className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-3 bg-black/85 p-4 outline-none"
    >
      <IconButton label="닫기" icon={X} variant="ghost" className="absolute right-3 top-3 text-white hover:bg-white/10" onClick={onClose} />
      {asset.Kind === 'image' ? (
        <img
          src={mediaFileUrl(asset.ID)}
          alt={asset.Filename}
          onClick={(e) => e.stopPropagation()}
          className="max-h-[82vh] max-w-full rounded object-contain shadow-overlay"
        />
      ) : (
        <video
          src={mediaFileUrl(asset.ID)}
          controls
          autoPlay
          onClick={(e) => e.stopPropagation()}
          className="max-h-[82vh] max-w-full rounded shadow-overlay"
        />
      )}
      <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-meta text-white/80">
        <span>{asset.Filename}</span>
        <span>{kb(asset.SizeBytes)}</span>
        <a
          href={mediaFileUrl(asset.ID)}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="underline hover:text-white"
        >
          원본 열기
        </a>
        <span className="text-white/50">Esc로 닫는다</span>
      </div>
    </div>,
    document.body,
  )
}

/** /media: the files posts can carry — upload, look, delete. */
export default function Media() {
  const q = useMedia()
  const upload = useUploadMedia()
  const remove = useDeleteMedia()
  const toast = useToast()
  const input = useRef<HTMLInputElement>(null)
  const [viewing, setViewing] = useState<MediaAsset | null>(null)
  const [deleting, setDeleting] = useState<MediaAsset | null>(null)
  const [inUse, setInUse] = useState<{ asset: MediaAsset; uses: MediaUse[] } | null>(null)
  const items = q.data?.items ?? []

  return (
    <>
      <PageHeader
        title="미디어"
        description={q.data ? `파일 ${items.length}개 — 글을 쓸 때 여기서 고른다.` : undefined}
        actions={
          <>
            <input
              ref={input}
              type="file"
              hidden
              multiple
              accept="image/*,video/*"
              aria-label="올릴 파일"
              onChange={async (e) => {
                const files = Array.from(e.target.files ?? [])
                e.target.value = ''
                if (files.length === 0) return
                try {
                  await upload.mutateAsync(files)
                  toast.success(`${files.length}개를 올렸다`)
                } catch {
                  /* the message shows under the header */
                }
              }}
            />
            <Button variant="primary" icon={Upload} loading={upload.isPending} onClick={() => input.current?.click()}>
              올리기
            </Button>
          </>
        }
      />
      {upload.error != null && <ErrorState className="mb-3" title="올리지 못했다" error={upload.error} />}

      {q.error ? (
        <ErrorState title="미디어를 불러오지 못했다" error={q.error} onRetry={() => q.refetch()} />
      ) : !q.data ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="aspect-[4/3]" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={ImageIcon}
          title="올린 파일이 없다"
          description="이미지나 영상을 올리면 데스크 글과 캠페인 글에 붙일 수 있다."
          actions={
            <Button variant="primary" icon={Upload} onClick={() => input.current?.click()}>
              올리기
            </Button>
          }
        />
      ) : (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {items.map((m) => (
            <li key={m.ID} className="overflow-hidden rounded-lg border border-border bg-card">
              {/* The whole frame, not a square crop: a 16:9 hero cropped to a
                  square shows about half of it, which is not enough to tell
                  whether the render is right. */}
              <button
                type="button"
                onClick={() => setViewing(m)}
                title="크게 보기"
                className="block aspect-[4/3] w-full overflow-hidden bg-muted"
              >
                <MediaThumb asset={m} className="object-contain" />
              </button>
              <div className="flex items-center gap-2 p-2.5">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-table font-medium" title={m.Filename}>
                    {m.Filename}
                  </div>
                  <div className="text-meta text-muted-foreground">
                    {m.Kind === 'image' ? '이미지' : '영상'} · {kb(m.SizeBytes)}
                  </div>
                </div>
                <IconButton label={`${m.Filename} 삭제`} icon={Trash2} size="sm" onClick={() => setDeleting(m)} />
              </div>
            </li>
          ))}
        </ul>
      )}

      {viewing && <Lightbox asset={viewing} onClose={() => setViewing(null)} />}
      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(null)}
        title={`${deleting?.Filename ?? ''} 파일을 지울까`}
        description="파일이 라이브러리에서 지워진다. 되돌릴 수 없다."
        confirmLabel="삭제"
        confirmVariant="destructive"
        onConfirm={async () => {
          if (!deleting) return
          try {
            await remove.mutateAsync(deleting.ID)
            toast.success('지웠다')
          } catch (e) {
            const uses = usesOf(e)
            if (uses) setInUse({ asset: deleting, uses })
            else toast.error(`지우지 못했다: ${errorText(e)}`)
          }
          setDeleting(null)
        }}
      />
      <Dialog
        open={!!inUse}
        onClose={() => setInUse(null)}
        title={`${inUse?.asset.Filename ?? ''} 파일은 지울 수 없다`}
        description={`아직 게시하지 않은 글 ${inUse?.uses.length ?? 0}건이 이 파일을 쓰고 있다. 글에서 파일을 빼거나 글이 게시된 뒤에 지운다.`}
        footer={
          <Button variant="primary" onClick={() => setInUse(null)}>
            확인
          </Button>
        }
      >
        <ul className="divide-y divide-border rounded-md border border-border text-table">
          {inUse?.uses.map((u) => (
            <li key={`${u.kind}-${u.id}`} className="flex flex-wrap items-baseline gap-x-2 px-3 py-2">
              <span className="font-medium">{KIND_LABEL[u.kind]}</span>
              <span className="text-muted-foreground">{u.where}</span>
              <span className="min-w-0 flex-1 truncate">{u.label || '(본문 없음)'}</span>
            </li>
          ))}
        </ul>
      </Dialog>
    </>
  )
}
